'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  fetchDynamicSections,
  fetchStoreServices,
} from '@eliadmin/core/src/api/slugs/website-content-api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import type { Service } from '../../types/service';
import { useQuery } from '@tanstack/react-query';
import { MdOutlineEmail, MdOutlineLocationOn, MdOutlinePhoneInTalk } from 'react-icons/md';
import { FaMinus } from 'react-icons/fa';
import type { TDynamicData } from '../../types/dynamic-data';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const CategoryItem = ({ service, layer }: { service: Service; layer?: number }) => {
  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={service.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal border-none',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <SheetClose asChild>
          <Link prefetchOnHover href={service.slug ?? '#'} prefetch={false}
            className={cn('ml-4 flex-1',
              'flex items-center justify-start gap-4 text-left text-base font-light uppercase text-black',
            )}
          >
            <FaMinus className="text-primary" />
            {service.name}
          </Link>
        </SheetClose>
      </li>
    </AccordionItem>
  );
};

const ProductLinkItem = ({ item, layer }: { item: TDynamicData; layer?: number }) => {
  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={item.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal border-none',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <SheetClose asChild>
          <Link prefetchOnHover target="_blank" href={item.fields[0]?.value} prefetch={false}
            className={cn('ml-4 flex-1',
              'flex items-center justify-start gap-4 text-left text-base font-light uppercase text-black',
            )}
          >
            <FaMinus className="text-primary" />
            {item.name}
          </Link>
        </SheetClose>
      </li>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const generalLayout = useGlobalStore(state => state.generalLayout);
  //const { t } = useTranslations();

  const { data: services = { data: [] } } = useQuery({
    queryKey: [CacheKey.REFERENCES, locale],
    queryFn: () => fetchStoreServices({ locale }),
  });

  const { data: productLinks = { data: [] } } = useQuery({
    queryKey: ['LINKS', locale],
    queryFn: () => fetchDynamicSections({ locale, sectionIds: [3] }),
  });

  const contacts = [
    {
      icon: <MdOutlinePhoneInTalk size={24} />,
      label: generalLayout.contact?.phone,
      href: `tel:${generalLayout.contact?.phone}`,
    },
    {
      icon: <MdOutlineEmail size={24} />,
      label: generalLayout.contact?.email,
      href: `mailto:${generalLayout.contact?.email}`,
    },
    {
      icon: <MdOutlineLocationOn size={24} />,
      label: `${generalLayout.contact?.address}, ${generalLayout.contact?.postcode} ${generalLayout.contact?.city}`,
      href: '#',
    },
  ];

  const { t } = useTranslations();

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={locale === 'sk' ? 'Otvoriť menu' : 'Open menu'}
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={'right'}
          className={cn(
            'flex w-screen flex-col items-start justify-start gap-12 overflow-scroll border-none p-6 pt-24',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <ul
            className={cn(
              'ea-menu-sidebar__list items-starts flex flex-col justify-start gap-3',
              // className?.list,
            )}
          >
            <Accordion
              type="single"
              collapsible
              className="flex flex-col items-start justify-start gap-3"
            >
              <AccordionItem value={t('text1000')}>
                <AccordionTrigger
                  className={cn(
                    'relative flex w-full items-center justify-between py-0 text-lg font-bold uppercase text-primary',
                    'ea-mobile-sidebar__accordion-first-trigger',
                  )}
                >
                  {t('text1000')}
                </AccordionTrigger>
                <AccordionContent className="mt-6 flex flex-col gap-6">
                  {services?.data.map((service, index) => (
                    <CategoryItem key={index} service={service} />
                  ))}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value={t('text150')}>
                <AccordionTrigger
                  className={cn(
                    'relative flex w-full items-center justify-between py-0 text-lg font-bold uppercase',
                    'ea-mobile-sidebar__accordion-first-trigger',
                  )}
                >
                  {t('text150')}
                </AccordionTrigger>
                <AccordionContent className="mt-6 flex flex-col gap-6">
                  {productLinks?.data.map((item, index) => (
                    <ProductLinkItem key={index} item={item} />
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {!!props.menu && (
              <div className="flex flex-col gap-3">
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full"
                        prefetch={false}
                      >
                        <div className={cn('text-lg font-bold', 'uppercase text-black')}>
                          {item.name}
                        </div>
                      </Comp>
                    </SheetClose>
                  );
                })}
              </div>
            )}
          </ul>
          <SheetClose asChild className="w-full">
            <Link prefetchOnHover
              prefetch={false}
              href={locale === 'en' ? '/#contact' : '/#kontakt'}
              className="flex w-full items-center justify-center bg-primary py-4 text-center font-medium uppercase text-white"
            >
              {t('text82')}
            </Link>
          </SheetClose>
          <div className="flex w-full flex-col items-start justify-start gap-3">
            <span className="text-xl font-bold uppercase text-muted-foreground">
              {t('text186')}
            </span>
            {contacts.map(contact => (
              <Link prefetchOnHover
                key={contact.href}
                prefetch={false}
                target="_blank"
                href={contact.href}
                className="flex items-center justify-start gap-3 font-medium text-primary"
              >
                {contact.icon}
                <span>{contact.label}</span>
              </Link>
            ))}
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-3">
            <span className="text-xl font-bold uppercase text-muted-foreground">
              {t('text690')}
            </span>
            <HtmlRendererRaw
              className={cn('w-full', 'ea-mobile-sidebar__text-container')}
              html={generalLayout.footer?.text}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
